import { useAPI } from "@/services/api";
import { light } from "@/services/light";
import { ButtonFooter } from "@Light/components/page/ButtonFooter";
import { useHeaderFloating } from "@Light/components/page/header/context";
import { MutationButton } from "@Light/components/page/MutationButton";
import { PageBody } from "@Light/components/page/PageBody";
import { useScaffold } from "@Light/scaffold";
import { appLoginRouteToPathOrDefault } from "@Light/services/standaloneLoginRoute";
import { useDispatch, useSearchParams } from "@Light/utils/context";
import { useEffect } from "react";
import { STANDALONE_LOGIN_LINK_EXPIRATION } from "./constants";

export const LoginToken: React.FC = () => {
  const searchParams = useSearchParams()[0];
  const token = searchParams.get("token");
  const dispatch = useDispatch();
  const scaffold = useScaffold();

  const { useLoginTokenMutation, useLoginResendEmailMutation } = useAPI();
  const [loginToken, loginTokenState] = useLoginTokenMutation();
  const [resendEmail, resendEmailState] = useLoginResendEmailMutation();

  useHeaderFloating(true);

  useEffect(() => {
    if (token) {
      loginToken({ token });
    }
  }, [token, loginToken]);

  useEffect(() => {
    if (loginTokenState.isSuccess) {
      dispatch(light.util.invalidateTags(["Account"]));
    }
  }, [loginTokenState.isSuccess, dispatch]);

  const Navigate = scaffold.system.navigate;
  const Spinner = scaffold.system.spinner;

  if (!token) {
    return <Navigate to="/login" />;
  }

  if (loginTokenState.isUninitialized || loginTokenState.isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner className="w-8 h-8" />
      </div>
    );
  }

  if (loginTokenState.isSuccess) {
    const route = appLoginRouteToPathOrDefault(loginTokenState.data?.route);
    return <Navigate to={route} replace />;
  }

  if (resendEmailState.isSuccess) {
    return <Navigate to="/login/check-email" />;
  }

  if (resendEmailState.isError) {
    return <Navigate to="/login" />;
  }

  return (
    <PageBody
      title="Your login link has expired"
      subtitle={`Login links expire in ${STANDALONE_LOGIN_LINK_EXPIRATION}.`}
      size="lg"
      centered
      footer={
        <ButtonFooter>
          <MutationButton
            type="button"
            size="md"
            buttonType="outline"
            mutation={resendEmailState}
            mutate={() => {
              resendEmail({ token });
            }}
            mutateButtonText="Send new link"
          />
        </ButtonFooter>
      }
    />
  );
};
